import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { countNewLeadsCreatedBetween } from '../lib/crm/countNewLeads';
import { getBusinessLeadCreatedAtRanges } from '../lib/crm/leadCreatedAtRanges';

/** New leads created today vs yesterday (by created_at, business timezone). */
export function useNewLeadsTodayMetrics() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  const [newLeadsToday, setNewLeadsToday] = useState(0);
  const [newLeadsYesterday, setNewLeadsYesterday] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setNewLeadsToday(0);
      setNewLeadsYesterday(0);
      setLoading(false);
      return;
    }

    try {
      const { todayStart, tomorrowStart, yesterdayStart } =
        getBusinessLeadCreatedAtRanges(timezone);

      const [todayCount, yesterdayCount] = await Promise.all([
        countNewLeadsCreatedBetween(supabase, businessId, todayStart, tomorrowStart),
        countNewLeadsCreatedBetween(supabase, businessId, yesterdayStart, todayStart),
      ]);

      setNewLeadsToday(todayCount);
      setNewLeadsYesterday(yesterdayCount);
    } catch (err) {
      console.error('Error loading new leads today metrics:', err);
      setNewLeadsToday(0);
      setNewLeadsYesterday(0);
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone]);

  useEffect(() => {
    setLoading(true);
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return { newLeadsToday, newLeadsYesterday, loading, refresh: load };
}
